<template>
  <header class="shop-header" :class="{ scrolled: isScrolled }">
    <div class="shop-header-inner">

      <!-- Mobile burger — visible only below the desktop nav breakpoint.
           Placed first so it sits at the leading edge regardless of logo
           width. The same `aria-expanded` drives the slide-in drawer. -->
      <button
        type="button"
        class="shop-burger"
        :aria-label="mobileNavOpen ? $t('shop.close_menu', 'Close menu') : $t('shop.open_menu', 'Open menu')"
        :aria-expanded="mobileNavOpen"
        @click="mobileNavOpen = !mobileNavOpen"
      >
        <svg v-if="!mobileNavOpen" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" class="w-5 h-5">
          <line x1="3" y1="6" x2="21" y2="6"/>
          <line x1="3" y1="12" x2="21" y2="12"/>
          <line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
        <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" class="w-5 h-5">
          <line x1="6" y1="6" x2="18" y2="18"/>
          <line x1="18" y1="6" x2="6" y2="18"/>
        </svg>
      </button>

      <!-- Logo -->
      <NuxtLink to="/" class="shop-logo">
        <AppLogo />
      </NuxtLink>

      <!-- Nav -->
      <nav class="shop-nav">
        <NuxtLink to="/" class="shop-nav-link" :class="{ active: isActive('/') }">{{ $t('shop.nav_releases') }}</NuxtLink>
        <ShopGenreNav />
        <NuxtLink to="/feed" class="shop-nav-link" :class="{ active: isActive('/feed') }">{{ $t('shop.nav_feed') }}</NuxtLink>
        <NuxtLink to="/artists" class="shop-nav-link" :class="{ active: isActive('/artists') }">{{ $t('shop.nav_artists') }}</NuxtLink>
        <NuxtLink v-if="tenant?.merchEnabled" to="/merch" class="shop-nav-link" :class="{ active: isActive('/merch') }">{{ $t('shop.nav_merch') }}</NuxtLink>
        <NuxtLink v-if="hasBundles" to="/bundles" class="shop-nav-link" :class="{ active: isActive('/bundles') }">{{ $t('shop_bundle.nav') }}</NuxtLink>
        <NuxtLink v-if="hasNews" to="/news" class="shop-nav-link" :class="{ active: isActive('/news') }">{{ $t('shop.nav_news') }}</NuxtLink>
        <!-- /products surfaces BOTH Audio Asset Bundles AND Audio Software
             (plugins, etc). The previous gate only checked audioAssetsEnabled,
             so a tenant who only sold plugins (audioSoftwareEnabled=true) had
             no nav entry, even though their products were live on /products. -->
        <NuxtLink v-if="tenant?.audioAssetsEnabled || tenant?.audioSoftwareEnabled" to="/products" class="shop-nav-link" :class="{ active: isActive('/products') }">{{ $t('shop.nav_samples') }}</NuxtLink>
        <NuxtLink v-if="tenant?.freeDownloadsEnabled" to="/free" class="shop-nav-link shop-nav-link-accent" :class="{ active: isActive('/free') }">{{ $t('shop.nav_free') }}</NuxtLink>
      </nav>

      <!-- Mobile search trigger — only on phones (<= 600px). Reveals the
           search input by toggling .shop-search.mobile-open. -->
      <button
        type="button"
        class="shop-search-trigger"
        :aria-label="$t('shop.search_placeholder', 'Search')"
        @click="openMobileSearch"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
      </button>

      <!-- Search -->
      <div class="shop-search" :class="{ focused: searchFocused, 'has-results': showDropdown, 'mobile-open': mobileSearchOpen }" ref="searchRef">
        <svg class="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="$t('shop.search_placeholder')"
          @focus="onSearchFocus"
          @blur="onSearchBlur"
          @input="handleSearchInput"
          @keyup.enter="handleSearch"
          @keydown.escape="closeDropdown"
        />
        <span v-if="searchLoading" class="search-spinner"></span>
        <button v-else-if="searchQuery" class="search-clear" @click="clearSearch">
          <svg viewBox="0 0 24 24" fill="currentColor" class="w-3.5 h-3.5"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
        </button>

        <!-- Dropdown -->
        <Transition name="search-drop">
          <div v-if="showDropdown" class="search-dropdown">
            <div v-if="hasResults">

              <!-- Releases -->
              <div v-if="searchResults.releases?.length" class="sdrop-section">
                <div class="sdrop-label">{{ $t('shop.nav_releases') }}</div>
                <NuxtLink
                  v-for="r in searchResults.releases"
                  :key="r._id"
                  :to="`/releases/${r.slug}`"
                  class="sdrop-item"
                  @click="closeDropdown"
                >
                  <img :src="getArtworkUrl(r)" class="sdrop-thumb sdrop-thumb-sq" :alt="r.name" />
                  <div class="sdrop-text">
                    <span class="sdrop-title">{{ r.name }}</span>
                    <span class="sdrop-sub">{{ r.displayArtist }}</span>
                  </div>
                  <span class="sdrop-tag">{{ r.releaseFormatType }}</span>
                </NuxtLink>
              </div>

              <!-- Artists -->
              <div v-if="searchResults.artists?.length" class="sdrop-section">
                <div class="sdrop-label">{{ $t('shop.nav_artists') }}</div>
                <NuxtLink
                  v-for="a in searchResults.artists"
                  :key="a._id"
                  :to="`/artists/${a.slug}`"
                  class="sdrop-item"
                  @click="closeDropdown"
                >
                  <img :src="getArtistImg(a)" class="sdrop-thumb sdrop-thumb-round" :alt="a.name" />
                  <div class="sdrop-text">
                    <span class="sdrop-title">{{ a.name }}</span>
                  </div>
                </NuxtLink>
              </div>

              <!-- Merch -->
              <div v-if="searchResults.merch?.length" class="sdrop-section">
                <div class="sdrop-label">{{ $t('shop.nav_merch') }}</div>
                <NuxtLink
                  v-for="m in searchResults.merch"
                  :key="m._id"
                  :to="`/merch/${m._id}`"
                  class="sdrop-item"
                  @click="closeDropdown"
                >
                  <img :src="getMerchImg(m)" class="sdrop-thumb sdrop-thumb-sq" :alt="m.name" />
                  <div class="sdrop-text">
                    <span class="sdrop-title">{{ m.name }}</span>
                    <span class="sdrop-sub">{{ m.baseProductName }}</span>
                  </div>
                  <span class="sdrop-price">€{{ Number(m.price).toFixed(2) }}</span>
                </NuxtLink>
              </div>

              <!-- Sample Packs -->
              <div v-if="searchResults.products?.length" class="sdrop-section">
                <div class="sdrop-label">{{ $t('shop.nav_samples') }}</div>
                <NuxtLink
                  v-for="p in searchResults.products"
                  :key="p._id"
                  :to="`/products/${p.slug || p._id}`"
                  class="sdrop-item"
                  @click="closeDropdown"
                >
                  <img :src="getProductImg(p)" class="sdrop-thumb sdrop-thumb-sq" :alt="p.title" />
                  <div class="sdrop-text">
                    <span class="sdrop-title">{{ p.title }}</span>
                  </div>
                  <span v-if="p.price" class="sdrop-price">€{{ Number(p.price).toFixed(2) }}</span>
                </NuxtLink>
              </div>

              <!-- View all -->
              <button class="sdrop-viewall" @click="handleSearch">
                {{ $t('shop.search_view_all', { q: searchQuery }) }}
                <svg viewBox="0 0 24 24" fill="currentColor" class="w-3.5 h-3.5"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
              </button>
            </div>

            <div v-else class="sdrop-empty">
              {{ $t('shop.search_no_results', { q: searchQuery }) }}
            </div>
          </div>
        </Transition>
      </div>

      <!-- Actions -->
      <div class="shop-actions">
        <!-- Cart -->
        <NuxtLink to="/cart" class="cart-btn" :aria-label="$t('shop.cart_aria')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" class="w-5 h-5">
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <path d="M16 10a4 4 0 0 1-8 0"/>
          </svg>
          <span v-if="itemCount" class="cart-badge">{{ itemCount }}</span>
        </NuxtLink>

        <!-- Auth -->
        <template v-if="loggedIn">
          <div class="user-menu-wrap" ref="menuRef">
            <button class="user-btn" @click="menuOpen = !menuOpen">
              <svg viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
              <span>{{ user?.name?.split(' ')[0] || $t('shop.my_account') }}</span>
              <svg viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3 opacity-50"><path d="M7 10l5 5 5-5z"/></svg>
            </button>
            <Transition name="menu-drop">
              <div v-if="menuOpen" class="user-dropdown">
                <NuxtLink to="/orders" class="dropdown-item" @click="menuOpen = false">
                  <svg viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path d="M20 6h-2.18c.07-.44.18-.88.18-1.34C18 2.54 15.55 0 12 0S6 2.54 6 4.66c0 .46.11.9.18 1.34H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-8-4c2.21 0 4 1.79 4 4H8c0-2.21 1.79-4 4-4zm8 20H4V8h16v14z"/></svg>
                  {{ $t('shop.my_orders') }}
                </NuxtLink>
                <NuxtLink to="/playlists" class="dropdown-item" @click="menuOpen = false">
                  <svg viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path d="M15 6H3v2h12V6zm0 4H3v2h12v-2zM3 16h8v-2H3v2zM17 6v8.18c-.31-.11-.65-.18-1-.18-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3V8h3V6h-5z"/></svg>
                  {{ $t('shop.playlists') }}
                </NuxtLink>
                <NuxtLink to="/account/preferences" class="dropdown-item" @click="menuOpen = false">
                  <svg viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z"/></svg>
                  {{ $t('genrePrefs.menuItem') }}
                </NuxtLink>
                <button class="dropdown-item" @click="handleLogout">
                  <svg viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/></svg>
                  {{ $t('shop.sign_out') }}
                </button>
              </div>
            </Transition>
          </div>
        </template>
        <template v-else>
          <NuxtLink to="/login" class="auth-link">{{ $t('shop.sign_in') }}</NuxtLink>
          <NuxtLink to="/signup" class="auth-btn">{{ $t('shop.get_started') }}</NuxtLink>
        </template>
      </div>
    </div>


    <!-- Mobile drawer — slides in from the left under the burger.
         Surfaces every navigation link plus auth shortcuts that the
         desktop header hides at <600px. Outside the .shop-header-inner
         flex row so it can occupy a full-width fixed panel below the
         60px bar without disturbing its layout. -->
    <Transition name="drawer-slide">
      <div v-if="mobileNavOpen" class="shop-drawer" role="dialog" :aria-label="$t('shop.menu', 'Menu')">
        <nav class="shop-drawer-nav">
          <NuxtLink to="/" class="drawer-link" :class="{ active: isActive('/') }" @click="mobileNavOpen = false">{{ $t('shop.nav_releases') }}</NuxtLink>
          <NuxtLink to="/feed" class="drawer-link" :class="{ active: isActive('/feed') }" @click="mobileNavOpen = false">{{ $t('shop.nav_feed') }}</NuxtLink>
          <NuxtLink to="/artists" class="drawer-link" :class="{ active: isActive('/artists') }" @click="mobileNavOpen = false">{{ $t('shop.nav_artists') }}</NuxtLink>
          <NuxtLink v-if="tenant?.merchEnabled" to="/merch" class="drawer-link" :class="{ active: isActive('/merch') }" @click="mobileNavOpen = false">{{ $t('shop.nav_merch') }}</NuxtLink>
          <NuxtLink v-if="hasBundles" to="/bundles" class="drawer-link" :class="{ active: isActive('/bundles') }" @click="mobileNavOpen = false">{{ $t('shop_bundle.nav') }}</NuxtLink>
          <NuxtLink v-if="hasNews" to="/news" class="drawer-link" :class="{ active: isActive('/news') }" @click="mobileNavOpen = false">{{ $t('shop.nav_news') }}</NuxtLink>
          <NuxtLink v-if="tenant?.audioAssetsEnabled || tenant?.audioSoftwareEnabled" to="/products" class="drawer-link" :class="{ active: isActive('/products') }" @click="mobileNavOpen = false">{{ $t('shop.nav_samples') }}</NuxtLink>
          <NuxtLink v-if="tenant?.freeDownloadsEnabled" to="/free" class="drawer-link" :class="{ active: isActive('/free') }" @click="mobileNavOpen = false">{{ $t('shop.nav_free') }}</NuxtLink>
        </nav>
        <!-- Genres — the desktop mega menu (GenreNav) lives in .shop-nav,
             which is display:none on phones; without this section mobile
             had no path into the genre browser at all. Same data (and
             fetch key, so it dedupes) as the mega menu. -->
        <template v-if="drawerGenres.length">
          <div class="shop-drawer-divider"></div>
          <div class="shop-drawer-genres">
            <span class="drawer-section-label">{{ $t('shop.nav_genres') }}</span>
            <div class="drawer-genre-chips">
              <NuxtLink
                v-for="g in drawerGenres"
                :key="g.slug"
                :to="`/genre/${g.slug}`"
                class="drawer-genre-chip"
                @click="mobileNavOpen = false"
              >
                {{ g.name }}<span v-if="g.total" class="drawer-genre-count">{{ g.total }}</span>
              </NuxtLink>
            </div>
          </div>
        </template>
        <div class="shop-drawer-divider"></div>
        <div class="shop-drawer-auth">
          <template v-if="loggedIn">
            <NuxtLink to="/orders" class="drawer-link" @click="mobileNavOpen = false">{{ $t('shop.my_orders') }}</NuxtLink>
            <NuxtLink to="/playlists" class="drawer-link" @click="mobileNavOpen = false">{{ $t('shop.playlists') }}</NuxtLink>
            <button class="drawer-link drawer-link-btn" @click="handleLogout(); mobileNavOpen = false">{{ $t('shop.sign_out') }}</button>
          </template>
          <template v-else>
            <NuxtLink to="/login" class="drawer-link" @click="mobileNavOpen = false">{{ $t('shop.sign_in') }}</NuxtLink>
            <NuxtLink to="/signup" class="drawer-link drawer-link-cta" @click="mobileNavOpen = false">{{ $t('shop.get_started') }}</NuxtLink>
          </template>
        </div>
      </div>
    </Transition>

    <!-- Scrim — taps anywhere outside the drawer to close. -->
    <Transition name="drawer-fade">
      <div
        v-if="mobileNavOpen"
        class="shop-drawer-scrim"
        @click="mobileNavOpen = false"
        aria-hidden="true"
      ></div>
    </Transition>
  </header>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { merchImageSrc } from '~/utils/merchImage'
const { cart, itemCount } = useCart()
const { user, loggedIn, logout } = useAuth()
const { tenant } = useTenantTheme()
// News nav entry only once the tenant has published news.
const { data: newsProbe } = await useFetch('/api/shop/news', { key: 'shop-news-nav', query: { limit: 1 } })
const hasNews = computed(() => (newsProbe.value?.total || 0) > 0)
// Bundles nav entry once the feature is on and at least one bundle is active.
const { data: bundlesProbe } = await useFetch('/api/shop/bundles', {
  key: 'shop-bundles-nav',
  query: { count: 1 },
  immediate: !!tenant.value?.bundlesEnabled
})
const hasBundles = computed(() => !!tenant.value?.bundlesEnabled && (bundlesProbe.value?.total || 0) > 0)

// Same endpoint + key as GenreNav's mega menu — useFetch dedupes on the
// key, so this costs no extra request. Drawer shows main genres only.
const { data: shopGenres } = await useFetch('/api/shop/genres', {
    key: 'shop-genres',
    default: () => []
})
const drawerGenres = computed(() => (shopGenres.value || []).filter(g => !g.parentKey))
const route = useRoute()
const { t } = useI18n()

const isScrolled = ref(false)
const searchFocused = ref(false)
const searchQuery = ref(route.query.q || '')
const menuOpen = ref(false)
// Mobile-only state. `mobileNavOpen` controls the slide-in drawer that
// replaces the hidden desktop nav; `mobileSearchOpen` reveals the search
// field on phones where the trigger icon stands in for the full input.
const mobileNavOpen = ref(false)
const mobileSearchOpen = ref(false)
const menuRef = ref(null)
const searchRef = ref(null)
const searchResults = ref({ releases: [], artists: [], merch: [], products: [] })
const searchLoading = ref(false)
const dropdownVisible = ref(false)
let searchTimer = null

const hasResults = computed(() =>
  searchResults.value.releases?.length ||
  searchResults.value.artists?.length ||
  searchResults.value.merch?.length ||
  searchResults.value.products?.length
)

const showDropdown = computed(() => dropdownVisible.value && searchQuery.value.length >= 2)

const isActive = (path) => {
  if (path === '/') return route.path === '/' || route.path.startsWith('/releases')
  return route.path.startsWith(path)
}

const fetchDropdown = async () => {
  const q = searchQuery.value.trim()
  if (q.length < 2) { searchResults.value = { releases: [], artists: [], merch: [], products: [] }; return }
  searchLoading.value = true
  try {
    const data = await $fetch(`/api/shop/search?q=${encodeURIComponent(q)}`)
    searchResults.value = data
    dropdownVisible.value = true
  } catch (err) {
    // Search-as-you-type: a single failure isn't user-facing, but we still
    // want it visible in the console so a regression on /api/shop/search
    // doesn't disappear silently.
    console.warn('[ShopHeader] search dropdown failed:', err)
  }
  searchLoading.value = false
}

const handleSearch = () => {
  closeDropdown()
  const q = searchQuery.value.trim()
  if (!q) { navigateTo(route.path); return }
  navigateTo(`${route.path}?q=${encodeURIComponent(q)}`)
}

const handleSearchInput = () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(fetchDropdown, 280)
}

const onSearchFocus = () => {
  searchFocused.value = true
  if (searchQuery.value.length >= 2 && hasResults.value) dropdownVisible.value = true
}

// Mobile search overlays the header on phones. When the input blurs we
// collapse the overlay so the action buttons (cart/burger) come back into
// view — but only after the click that may have landed on the dropdown
// has had a chance to fire (hence the small delay matching the dropdown's
// own outside-click handler).
const onSearchBlur = () => {
  searchFocused.value = false
  setTimeout(() => {
    if (!searchQuery.value) mobileSearchOpen.value = false
  }, 120)
}

const closeDropdown = () => { dropdownVisible.value = false }

const clearSearch = () => {
  searchQuery.value = ''
  searchResults.value = { releases: [], artists: [], merch: [], products: [] }
  dropdownVisible.value = false
  navigateTo('/')
}

const getArtworkUrl = (r) => {
  const art = r.artwork
  const url = art?.low?.url || art?.thumbnail?.url || art?.original?.url || art?.url
  if (url) return `/api/assets/serve?url=${encodeURIComponent(url)}`
  return 'https://placehold.co/48x48/0e0b1a/2a2040?text=•'
}

const getArtistImg = (a) => {
  if (a.imageUrl) return `/api/assets/serve?url=${encodeURIComponent(a.imageUrl)}`
  return 'https://placehold.co/48x48/0e0b1a/2a2040?text=A'
}

const getMerchImg = (m) => {
  // Same fallback as the catalog card — previewImages is usually empty
  // until Shirtigo has rendered the product photo.
  const url = m.previewImages?.[0]?.url || m.previewImages?.[0] || m.imageUrl || m.baseProductThumbnail
  return merchImageSrc(url) || 'https://placehold.co/48x48/0e0b1a/2a2040?text=M'
}

const getProductImg = (p) => {
  // The Product schema's image field is `imageUrl`. The previous template
  // read `coverImage` which never existed, so every sample-pack search
  // result fell through to the placeholder. Keep coverImage as a legacy
  // fallback in case any older payload still ships it.
  const url = p.imageUrl || p.coverImage?.url || p.coverImage
  if (!url) return 'https://placehold.co/48x48/0e0b1a/2a2040?text=S'
  if (url.startsWith('http')) return `/api/assets/serve?url=${encodeURIComponent(url)}`
  return url
}

const handleLogout = async () => {
  menuOpen.value = false
  await logout()
}

watch(() => route.query.q, (q) => { searchQuery.value = q || '' })

// Close the mobile drawer + search overlay whenever the route changes —
// otherwise tapping a nav link inside the drawer leaves the panel covering
// the destination page until the user taps the scrim.
watch(() => route.fullPath, () => {
  mobileNavOpen.value = false
  mobileSearchOpen.value = false
})

// Lock body scroll while the drawer is open so the underlying page doesn't
// scroll under the panel on iOS Safari.
watch(mobileNavOpen, (open) => {
  if (typeof document === 'undefined') return
  document.body.style.overflow = open ? 'hidden' : ''
})

const openMobileSearch = () => {
  mobileSearchOpen.value = true
  nextTick(() => {
    const el = searchRef.value?.querySelector('input')
    el?.focus()
  })
}

// Listeners stored in refs so onBeforeUnmount can remove them. Anonymous
// arrow functions inside addEventListener cannot be unregistered, so the
// scroll + outside-click listeners survived every navigation and leaked
// memory across SPA route changes.
const onScroll = () => { isScrolled.value = window.scrollY > 20 }
const onDocClick = (e) => {
  if (menuRef.value && !menuRef.value.contains(e.target)) menuOpen.value = false
  if (searchRef.value && !searchRef.value.contains(e.target)) dropdownVisible.value = false
}
onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
  document.addEventListener('click', onDocClick)
})
onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
  document.removeEventListener('click', onDocClick)
  if (searchTimer) clearTimeout(searchTimer)
  // Belt + suspenders: if the header unmounts while the mobile drawer
  // was still open (e.g. hot reload), we'd leave the body permanently
  // scroll-locked. Reset on unmount.
  if (typeof document !== 'undefined') document.body.style.overflow = ''
})
</script>

<style scoped>
.shop-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  /* Height is the 60px bar. Everything that must clear the chrome
     reads --cy-chrome-h (shop-cyber.css). */
  z-index: 100;
  border-bottom: 1px solid transparent;
  transition: background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
}

.shop-header.scrolled {
  /* Translucent ground + blur — chrome may blur (refined-look
     exemption 3); scrolling LISTS may not. */
  background: color-mix(in srgb, var(--cy-ground) 86%, transparent);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-bottom-color: var(--cy-rim);
  box-shadow: 0 14px 34px -20px rgba(0, 0, 0, 0.85);
}

.shop-header-inner {
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 24px;
  height: 60px;
  display: flex;
  align-items: center;
  gap: 24px;
  /* The mobile search field becomes `position: absolute` below the 600px
     breakpoint; anchor that to the inner row, not the page. */
  position: relative;
}

/* Logo */
.shop-logo {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.shop-logo :deep(.logo-img) {
  height: 32px;
  filter: drop-shadow(0 0 10px rgb(var(--primary-channels) / 0.22));
}

@media (max-width: 768px) {
  .shop-logo :deep(.logo-img) {
    height: 28px;
  }
}

/* Nav */
.shop-nav {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

/* Nav-link typography lives in shop-cyber.css (`.shop-layout .shop-nav
   .shop-nav-link`) so GenreNav's own scoped copy of the class gets the
   identical grammar. Only structural props stay local. */
.shop-nav-link {
  position: relative;
  text-decoration: none;
  transition: all 0.15s ease;
}

/* Search */
.shop-search {
  flex: 1;
  max-width: 360px;
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  width: 15px;
  height: 15px;
  color: var(--text-dim);
  pointer-events: none;
  transition: color 0.2s;
}

.shop-search input {
  width: 100%;
  height: 36px;
  padding: 0 36px 0 36px;
  background: color-mix(in srgb, var(--cy-ground) 60%, transparent);
  border: 1px solid var(--cy-rim);
  border-radius: 3px;
  color: var(--text-main);
  font-size: 0.78rem;
  outline: none;
  transition: all 0.2s ease;
  font-family: var(--cy-mono);
  letter-spacing: 0.02em;
}

.shop-search input::placeholder { color: var(--text-dim); }

.shop-search.focused input {
  border-color: var(--cy-rim-hot);
  background: rgb(var(--primary-channels) / 0.04);
  box-shadow: 0 0 0 3px rgb(var(--primary-channels) / 0.08), var(--cy-glow);
}

.shop-search.focused .search-icon { color: var(--primary); }

.search-spinner {
  position: absolute;
  right: 11px;
  width: 14px;
  height: 14px;
  border: 2px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.search-clear {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  color: var(--text-dim);
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 2px;
  transition: color 0.15s;
}
.search-clear:hover { color: var(--text-main); }

/* Search Dropdown */
.search-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: var(--cy-module);
  border: 1px solid var(--cy-rim);
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 16px 40px rgba(0,0,0,0.5), var(--cy-glow);
  z-index: 300;
  max-height: 480px;
  overflow-y: auto;
}

.search-dropdown::-webkit-scrollbar { width: 4px; }
.search-dropdown::-webkit-scrollbar-track { background: transparent; }
.search-dropdown::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }

.sdrop-section { padding: 6px 0; }
.sdrop-section + .sdrop-section { border-top: 1px solid var(--border); }

.sdrop-label {
  font-family: var(--cy-mono);
  font-size: 0.58rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgb(var(--primary-channels) / 0.75);
  padding: 6px 14px 4px;
}

.sdrop-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 14px;
  text-decoration: none;
  transition: background 0.12s;
  cursor: pointer;
}

.sdrop-item:hover { background: rgb(var(--primary-channels) / 0.06); }

.sdrop-thumb {
  width: 36px;
  height: 36px;
  object-fit: cover;
  flex-shrink: 0;
  background: var(--bg-color);
}

.sdrop-thumb-sq { border-radius: 4px; }
.sdrop-thumb-round { border-radius: 50%; }

.sdrop-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.sdrop-title {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-main);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sdrop-sub {
  font-size: 0.68rem;
  color: var(--text-dim);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sdrop-tag {
  font-size: 0.55rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-dim);
  background: rgb(255 255 255 / 0.04);
  border: 1px solid var(--border);
  border-radius: 3px;
  padding: 2px 5px;
  flex-shrink: 0;
}

.sdrop-price {
  font-family: var(--cy-mono);
  font-variant-numeric: tabular-nums;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--primary);
  flex-shrink: 0;
}

.sdrop-viewall {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px 14px;
  border-top: 1px solid var(--border);
  background: none;
  border-left: none;
  border-right: none;
  border-bottom: none;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--primary);
  cursor: pointer;
  transition: background 0.12s;
}

.sdrop-viewall:hover { background: rgb(var(--primary-channels) / 0.06); }

.sdrop-empty {
  padding: 24px 14px;
  text-align: center;
  font-size: 0.8rem;
  color: var(--text-dim);
}

/* Dropdown transition */
.search-drop-enter-active, .search-drop-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.search-drop-enter-from, .search-drop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* Actions */
.shop-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
}

.cart-btn {
  position: relative;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  color: var(--text-muted);
  transition: all 0.15s;
  background: rgb(255 255 255 / 0.03);
  border: 1px solid var(--cy-rim);
}

.cart-btn:hover {
  color: var(--primary);
  border-color: var(--cy-rim-hot);
  box-shadow: var(--cy-glow);
}

.cart-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  background: var(--primary);
  color: var(--bg-color);
  border-radius: 999px;
  font-size: 0.6rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* User menu */
.user-menu-wrap { position: relative; }

.user-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  background: rgb(255 255 255 / 0.03);
  border: 1px solid var(--cy-rim);
  border-radius: 3px;
  color: var(--text-muted);
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.user-btn:hover {
  color: var(--text-main);
  border-color: var(--cy-rim-hot);
}

.user-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 160px;
  background: var(--cy-module);
  border: 1px solid var(--cy-rim);
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 16px 40px rgba(0,0,0,0.5);
  z-index: 200;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  font-size: 0.8rem;
  color: var(--text-muted);
  transition: all 0.15s;
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
}

.dropdown-item:hover {
  background: rgb(255 255 255 / 0.04);
  color: var(--text-main);
}

.dropdown-item + .dropdown-item { border-top: 1px solid var(--border); }

/* Auth */
.auth-link {
  padding: 0 12px;
  height: 36px;
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-muted);
  transition: color 0.15s;
  text-decoration: none;
}
.auth-link:hover { color: var(--text-main); }

.auth-btn {
  height: 36px;
  padding: 0 16px;
  background: var(--primary);
  color: var(--cy-ground);
  font-family: var(--cy-display);
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  border-radius: 2px;
  transition: all 0.15s;
  text-decoration: none;
  display: flex;
  align-items: center;
  letter-spacing: 0.1em;
}
.auth-btn:hover {
  opacity: 0.94;
  box-shadow: var(--cy-glow);
}

/* Dropdown transition */
.menu-drop-enter-active, .menu-drop-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.menu-drop-enter-from, .menu-drop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ─── Mobile burger ─── */
.shop-burger {
  /* Hidden by default — the desktop layout never needs it. The drawer
     breakpoint below flips it on. Built as a 36-square so it matches
     the cart/user buttons on the right side visually. */
  display: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-right: 4px;
  background: rgb(255 255 255 / 0.03);
  border: 1px solid var(--cy-rim);
  border-radius: 3px;
  color: var(--text-muted);
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.15s, border-color 0.15s;
}
.shop-burger:hover { color: var(--primary); border-color: var(--cy-rim-hot); }

/* Mobile-only search trigger; the full input is hidden on phones and
   slid in via the .mobile-open class so we recoup the header width. */
.shop-search-trigger {
  display: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgb(255 255 255 / 0.03);
  border: 1px solid var(--cy-rim);
  border-radius: 3px;
  color: var(--text-muted);
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.15s, border-color 0.15s;
}
.shop-search-trigger:hover { color: var(--primary); border-color: var(--cy-rim-hot); }

/* ─── Mobile drawer ─── */
.shop-drawer {
  position: fixed;
  top: var(--cy-chrome-h, 60px);
  left: 0;
  width: min(88vw, 320px);
  /* `100dvh - chrome` keeps the panel from spilling under iOS browser
     chrome; older browsers fall back to the vh calc which is also fine
     (we just scroll the drawer's own overflow). */
  height: calc(100dvh - var(--cy-chrome-h, 60px));
  max-height: calc(100vh - var(--cy-chrome-h, 60px));
  background: var(--cy-module);
  border-right: 1px solid var(--cy-rim);
  z-index: 110;
  overflow-y: auto;
  padding: 16px 12px 32px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.shop-drawer-scrim {
  position: fixed;
  inset: var(--cy-chrome-h, 60px) 0 0 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 105;
  backdrop-filter: blur(2px);
}

.shop-drawer-nav,
.shop-drawer-auth {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.shop-drawer-divider {
  height: 1px;
  background: var(--border);
  margin: 16px 8px;
}

.drawer-link {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  font-family: var(--cy-display);
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-muted);
  border-radius: 3px;
  text-decoration: none;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.drawer-link:hover { background: rgb(var(--primary-channels) / 0.08); color: var(--text-main); }
.drawer-link.active {
  color: var(--primary);
  background: rgb(var(--primary-channels) / 0.10);
}
.drawer-link-btn { width: 100%; font-family: inherit; }
.drawer-link-cta {
  background: var(--primary);
  color: var(--bg-color);
  font-weight: 800;
  margin-top: 4px;
  justify-content: center;
}
.drawer-link-cta:hover { opacity: 0.92; color: var(--bg-color); background: var(--primary); }

/* Drawer genre section — chips wrap, matching the HUD chip grammar. */
.shop-drawer-genres {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 8px;
}
.drawer-section-label {
  font-family: var(--cy-mono);
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--text-dim);
  padding: 2px 6px 0;
}
.drawer-genre-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.drawer-genre-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border: 1px solid var(--cy-rim);
  border-radius: 3px;
  font-family: var(--cy-mono);
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
  text-decoration: none;
  transition: border-color 0.15s, color 0.15s;
}
.drawer-genre-chip:hover {
  border-color: var(--cy-rim-hot);
  color: var(--primary);
}
.drawer-genre-count {
  color: var(--text-dim);
  font-size: 0.6rem;
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: transform 0.22s ease, opacity 0.22s ease;
}
.drawer-slide-enter-from,
.drawer-slide-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.drawer-fade-enter-active,
.drawer-fade-leave-active { transition: opacity 0.22s ease; }
.drawer-fade-enter-from,
.drawer-fade-leave-to { opacity: 0; }

/* ─── Responsive ─── */
/* Tablet (≤ 960px): drop the inline nav for the burger drawer. The
   bumped breakpoint (was 900) keeps the GenreNav popover from squeezing
   the search input dangerously narrow before we collapse. */
@media (max-width: 960px) {
  .shop-burger { display: inline-flex; }
  .shop-nav { display: none; }
  .shop-search { max-width: 260px; }
}

/* Phone (≤ 600px): swap the full search input for a trigger icon so
   the logo + actions row stays uncluttered. The full input slides
   over the header when toggled. Auth-link "Sign in" hidden — both
   auth options are still reachable through the drawer. */
@media (max-width: 600px) {
  .shop-header-inner { gap: 8px; padding: 0 14px; }
  .shop-search-trigger { display: inline-flex; }
  .shop-search {
    /* Hidden by default on phones; revealed by .mobile-open. */
    position: absolute;
    top: 50%;
    left: 14px;
    right: 14px;
    transform: translateY(-50%);
    max-width: none;
    background: var(--surface-color);
    z-index: 4;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease;
  }
  .shop-search.mobile-open {
    opacity: 1;
    pointer-events: auto;
  }
  .shop-search.mobile-open input {
    border-color: rgb(var(--primary-channels) / 0.5);
    background: var(--surface-color);
  }
  .auth-link { display: none; }
  /* The display-face CTA is wider than the old sentence-case one —
     compress it so the 360px header row never overflows. */
  .auth-btn { padding: 0 10px; font-size: 0.62rem; letter-spacing: 0.06em; }
  /* User-menu button: collapse text label, just show the avatar icon. */
  .user-btn span { display: none; }
  .user-btn { padding: 0 10px; }
  /* Every other row member is a fixed-width control (burger, search
     trigger, cart, auth button) with flex-shrink: 0 — a wide wordmark
     logo at 28px height was the one thing that could push the bar past
     the viewport. Make the logo the elastic member: let it shrink and
     scale the image down proportionally (left-anchored) instead. */
  .shop-logo { flex-shrink: 1; min-width: 0; overflow: hidden; }
  .shop-logo :deep(.logo-img) {
    max-width: 100%;
    object-fit: contain;
    object-position: left center;
  }
}

/* Very narrow phones (≤ 380px) — drop the user-button entirely; the
   drawer surfaces "Orders" + "Sign out" the same way. */
@media (max-width: 380px) {
  .user-menu-wrap { display: none; }
  .shop-header-inner { gap: 6px; padding: 0 10px; }
  .auth-btn { padding: 0 8px; font-size: 0.58rem; letter-spacing: 0.04em; }
}
</style>
